import { forEach as _forEach } from 'lodash';
import validator from 'formValidation/Validator';

import { Entity as BaseEntity } from '@chedri/base';

const getterName = (name, suffix = '') => 'get' + name.charAt(0).toUpperCase() + name.substring(1) + suffix;

export default class Entity extends BaseEntity {
  validators = validator;

  /**
   * Override the base validate method to add safety checks
   */
  validate = () => {
    this.isValid = true; // initial value; if we find some errors, the value will be false
    this.errors = [];

    // Safety check: ensure metadata is properly loaded before validation
    if (!this.meta || !this.mainMetaIdentifier || !this.meta[this.mainMetaIdentifier] || !this.meta[this.mainMetaIdentifier].data) {
      console.warn('Entity metadata not properly loaded, skipping validation');
      return this;
    }

    this.loopValidate(this.processedData, this.mainMetaIdentifier, []);
    this.loopExtraValidators();

    this.key++;

    return this;
  };

  /**
   * Override the base loopValidate method to add safety checks for metadata
   */
  loopValidate = (data, metaIdentifier, fieldTree = []) => {
    // Safety check: ensure metadata exists before accessing it
    if (!this.meta || !this.meta[metaIdentifier] || !this.meta[metaIdentifier].data) {
      // Check if this is a Model class (which doesn't have validation metadata)
      if (metaIdentifier && metaIdentifier.includes('\\Model\\')) {
        // Model classes don't have validation metadata, this is expected
        return;
      }

      console.warn(`Entity metadata not available for identifier: ${metaIdentifier}`);
      return;
    }

    const meta = this.meta[metaIdentifier].data;
    const metaKeys = Object.keys(meta);
    for (let i = 0; i < metaKeys.length; i++) {
      const fieldName = metaKeys[i];
      if (Object.prototype.hasOwnProperty.call(meta, fieldName)) {
        const fieldMeta = meta[fieldName];

        if (
          fieldMeta.complexType !== undefined &&
          fieldMeta.complexType &&
          fieldMeta.array !== undefined &&
          fieldMeta.array
        ) {
          // this field is a complex type and is an array
          if (
            data[fieldMeta.name] !== undefined &&
            Object.prototype.toString.call(data[fieldMeta.name]) === '[object Array]'
          ) {
            if (fieldMeta.ref === undefined || !fieldMeta.ref) {
              // it's not a referenced object, we need to validate it
              Object.keys(data[fieldMeta.name]).forEach((key) => {
                const newFieldTree = fieldTree.slice();
                newFieldTree.push(fieldName);
                newFieldTree.push(key);
                this.loopValidate(data[fieldName][key], fieldMeta.type, newFieldTree);
              });
            } else if (fieldMeta.validate && fieldMeta.validate.rules && fieldMeta.validate.rules.indexOf('required') !== -1) {
              /* Validation was fixed and tested after the package update to version 13.15.35.
               * The rules for this are declared in the backend and should be sent with the form data.
               * This allows validation to be performed before submission. However, in most cases,
               * the meta information obtained via `getFieldNameMetaInformation` appears to be empty.
               * /api/entitymetadata.json?metaIdentifier=ClientBundle%5CDocument%5CClient&_locale - Meta.php
               */

              // it's an array of referenced objects, but it is required. check if user has selected at least one

              if (data[fieldMeta.name] === undefined || data[fieldMeta.name].length === 0) {
                this.updateValidation(
                  fieldMeta.name,
                  { valid: false, rule: 'required', helpText: fieldMeta.validate.messages.required },
                  fieldTree
                );
              } else {
                this.updateValidation(fieldMeta.name, { valid: true }, fieldTree);
              }
            }
          }
        } else if (fieldMeta.complexType !== undefined && fieldMeta.complexType) {
          // this field is complex type, but not an array
          if (fieldMeta.ref === undefined || !fieldMeta.ref) {
            if (
              (fieldMeta.validate && fieldMeta.validate.rules && fieldMeta.validate.rules.indexOf('required') !== -1) ||
              (data[fieldName] && data[fieldName].id)
            ) {
              const originalValue = this.getOriginalValueByPath([...fieldTree, fieldName]);
              if (originalValue) {
                // it's not a referenced object, we need to validate it
                const newFieldTree = fieldTree.slice();
                newFieldTree.push(fieldName);
                this.loopValidate(data[fieldName] !== undefined ? data[fieldName] : {}, fieldMeta.type, newFieldTree);
              }
              if (fieldMeta.validate && fieldMeta.validate.rules && fieldMeta.validate.messages) {
                const fieldRules = this.getValidationRules(fieldMeta.validate.rules, fieldMeta.validate.groups, data);
                const validate = this.validateOne(originalValue, fieldRules, fieldMeta.validate.messages);
                if (!validate.valid) {
                  this.updateValidation(fieldName, validate, fieldTree);
                } else {
                  this.updateValidation(fieldName, { valid: true }, fieldTree);
                }
              }
            } else {
              this.updateValidation(fieldMeta.name, { valid: true }, fieldTree);
            }
          } else if (fieldMeta.validate && fieldMeta.validate.rules && fieldMeta.validate.rules.indexOf('required') !== -1) {
            // it's a referenced object, but it is required. check if user has set an object
            let hasID = false;
            if (fieldMeta.type === 'ClientBundle\\Document\\Client') {
              hasID = !!(data[fieldName] !== undefined && data[fieldName].username);
            } else {
              hasID = !!(data[fieldName] !== undefined && data[fieldName].id);
            }

            // check if the group requirement does take place
            if (fieldMeta.validate.rules && fieldMeta.validate.groups) {
              const fieldRules = this.getValidationRules(fieldMeta.validate.rules, fieldMeta.validate.groups, data);
              if (fieldRules.findIndex(rule => rule.name === 'required') === -1) {
                hasID = true;
              }
            }

            if (!hasID) {
              this.updateValidation(
                fieldMeta.name,
                { valid: false, rule: 'required', helpText: fieldMeta.validate.messages.required },
                fieldTree
              );
            } else {
              this.updateValidation(fieldMeta.name, { valid: true }, fieldTree);
            }
          }
        } else if (fieldMeta.array !== undefined && fieldMeta.array) {
          // this is an array of simple type data
          if (data[fieldName] !== undefined && Object.prototype.toString.call(data[fieldName]) === '[object Array]') {
            if (fieldMeta.validate && fieldMeta.validate.rules && fieldMeta.validate.rules.indexOf('required') !== -1) {
              const fieldRules = this.getValidationRules(fieldMeta.validate.rules, fieldMeta.validate.groups, data);
              let arrayValid = true;
              Object.keys(data[fieldName]).forEach((key) => {
                // data[ fieldMeta.name ][key]
                const validate = this.validateOne(data[fieldName][key], fieldRules, fieldMeta.validate.messages);

                const newFieldTree = fieldTree.slice();
                if (fieldMeta.complexType) {
                  newFieldTree.push(key);
                }

                if (!validate.valid) {
                  this.updateValidation(fieldName, validate, newFieldTree);
                  arrayValid = false;
                } else {
                  this.updateValidation(fieldName, { valid: true }, newFieldTree);
                }
              });

              this.updateValidation(fieldName, { valid: arrayValid }, fieldTree);
            }
          }
        }
        // data[ fieldMeta.name ] !== undefined ? data[ fieldMeta.name ] : null;
        // validate only if the field is required or if a value is set
        else if (
          fieldMeta.validate &&
          fieldMeta.validate.rules &&
          (fieldMeta.validate.rules.indexOf('required') !== -1 || (data[fieldName] !== undefined && data[fieldName]))
        ) {
          const fieldRules = this.getValidationRules(fieldMeta.validate.rules, fieldMeta.validate.groups, data);
          const validate = this.validateOne(data[fieldName], fieldRules, fieldMeta.validate.messages);

          if (!validate.valid) {
            this.updateValidation(fieldName, validate, fieldTree);
          } else {
            this.updateValidation(fieldName, { valid: true }, fieldTree);
          }
        }
      }
    }
  };

  /**
   * Updated version of the @chedri/base@1.4.3
   * /src/forms/Entity.js
   * fixing the infinite loop while parsing Metadata.
   * Now we're checking if the current fieldName is already present in the fieldTree.
   *
   * @param {*} data
   * @param {string} metaIdentifier
   * @param {[array]} fieldTree
   * @param {[boolean]} useThisWhenTreeEmpty
   * @returns {Object}
   */
  loopMeta = (data, metaIdentifier, fieldTree = [], useThisWhenTreeEmpty = true) => {
    const result = {};
    if (!this.meta[metaIdentifier]) {
      return data;
    }
    const meta = this.meta[metaIdentifier].data;
    const metaKeys = Object.keys(meta);
    for (let i = 0; i < metaKeys.length; i++) {
      const fieldName = metaKeys[i];
      if (Object.prototype.hasOwnProperty.call(meta, fieldName)) {
        const fieldMeta = meta[fieldName];
        const fieldData = data && (data[fieldName] || data[fieldMeta.name]);

        if (fieldMeta.complexType && fieldMeta.array) {
          // this field is a complex type and is an array
          const values = [];
          if (fieldData) {
            _forEach(data[fieldName] || data[fieldMeta.name], (value, key) => {
              const newFieldTree = fieldTree.slice();
              newFieldTree.push(fieldName);
              values[key] = this.loopMeta(value ?? {}, fieldMeta.type, newFieldTree, useThisWhenTreeEmpty);
            });
          }
          result[fieldName] = values;
        } else if (fieldMeta.complexType) {
          /*
            Here an infinite loop could occur, when a current type has self type down in the field tree.
            In example: Client->parent (Client type again).
            Loop can be longer:
              target(Client)->parent->effectiveSuccessManager->parent->successManager
          */

          // count current fieldName occurence in the existing fieldTree
          const repeatCount = fieldTree.reduce((acc, c) => (c === fieldName ? acc + 1 : acc), 0);
          const maxRepeatitionsWithinTree = 2;

          if (
            fieldName === fieldTree[0] ||
            fieldName === fieldTree[fieldTree.length - 1] ||
            (repeatCount > 0 && fieldMeta.type === metaIdentifier) ||
            repeatCount > maxRepeatitionsWithinTree
          ) {
            // console.log(
            //   `[Entity] repeats x${repeatCount} "${metaIdentifier}.${fieldName}"\n\tTree: [${fieldTree.join(', ')}]`
            // );

            result[fieldName] = null;
          } else {
            // no repeatitions, all fine
            // this field is complex type, but not an array
            const newFieldTree = fieldTree.slice();
            newFieldTree.push(fieldName);
            result[fieldName] = this.loopMeta(fieldData || {}, fieldMeta.type, newFieldTree, useThisWhenTreeEmpty);
          }
        } else if (fieldMeta.array) {
          // this is an array of simple type data
          const values = [];
          if (fieldData) {
            _forEach(fieldData, (value, key) => {
              values[key] = value;
            });
          }
          result[fieldName] = values;
        } else if (fieldData) {
          result[fieldName] = fieldData;
        } else if (fieldData === false) {
          // save the false value not as Null, but as a Boolean false
          result[fieldName] = false;
        } else if (fieldData === 0) {
          result[fieldName] = 0;
        } else {
          result[fieldName] = null;
        }

        // assign get methods
        const source = fieldTree.length === 0 && useThisWhenTreeEmpty ? this : result;
        source[getterName(fieldName)] = () => result[fieldName];
        source[getterName(fieldName, 'MetaInformation')] = () => fieldMeta;
        source[getterName(fieldName, 'ValidationResult')] = () => ({ valid: true });
      }
    }

    return result;
  };
}
